/**
 * SuggestedPromptChips — Single-row Wrangle-style suggestion badges.
 */

import {
  User,
  Sparkles,
  BarChart2,
  FileText,
  PenLine,
  Mail,
  Wand2,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AGENT_CAPABILITY_PRESETS,
  type AgentCapabilityId,
  type AgentCapabilityPreset,
} from '../capabilityPrompts';

const ICON_MAP = {
  User,
  Sparkles,
  BarChart2,
  FileText,
  PenLine,
  Mail,
  Wand2,
} as const;

const CHIP_I18N_KEY: Record<AgentCapabilityId, string> = {
  'profile-assessment': 'agent.chips.profileAssessment.label',
  'recommended-jobs': 'agent.chips.recommendedJobs.label',
  'optimize-resume': 'agent.chips.optimizeResume.label',
  'cover-letter': 'agent.chips.coverLetter.label',
  'application-letter': 'agent.chips.applicationLetter.label',
  'build-resume': 'agent.chips.buildResume.label',
};

export interface SuggestedPromptChipsProps {
  onSelectChat: (preset: AgentCapabilityPreset) => void;
  onDeepLink:   (preset: AgentCapabilityPreset) => void;
  onAction:     (preset: AgentCapabilityPreset) => void;
}

const SuggestedPromptChips: React.FC<SuggestedPromptChipsProps> = ({
  onSelectChat,
  onDeepLink,
  onAction,
}) => {
  const { t } = useTranslation();

  const HIDDEN_CHIPS = new Set<AgentCapabilityId>(['optimize-resume', 'cover-letter', 'application-letter']);

  return (
    <div className="agent-suggested-chips mx-auto flex w-full max-w-[760px] flex-wrap items-center justify-center gap-2 overflow-visible">
      {AGENT_CAPABILITY_PRESETS.filter((p) => !HIDDEN_CHIPS.has(p.id)).map((preset) => {
        const Icon = ICON_MAP[preset.icon];
        const label = t(CHIP_I18N_KEY[preset.id], { defaultValue: preset.label });
        return (
          <button
            key={preset.id}
            type="button"
            onClick={() => {
              if (preset.kind === 'action')        onAction(preset);
              else if (preset.kind === 'deeplink') onDeepLink(preset);
              else onSelectChat(preset);
            }}
            className="agent-suggested-chips__pill ds-chip ds-chip-button shrink-0"
            aria-selected="false"
          >
            <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
            <span className="whitespace-nowrap">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default SuggestedPromptChips;
